// assets/AllCourses/Exercises/CompleteTheConversationExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import SpeakerButton from "@/components/ui/SpeakerButton";
import useSpeech from "@/hooks/useSpeech";
import { cn } from "@/utils/utils";

export default function CompleteTheConversationExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";
  const { speak, stop } = useSpeech();

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise?.conversationQuestions)
    ? exercise.conversationQuestions
    : [];

const sanitizeForSpeech = (text = "") => {
  return String(text)
    // 2 or more underscores → "blank"
    .replace(/_{2,}/g, " blank ")
    // single underscore → "underscore"
    .replace(/_/g, " underscore ")
    // clean extra spaces
    .replace(/\s+/g, " ")
    .trim();
};


  /* ================= STUDENT STATE ================= */
  const [answers, setAnswers] = useState({});
  const completedRef = useRef(false);
useEffect(() => {
  if (!showResults) completedRef.current = false;
}, [showResults]);
  /* ================= SYNC SAVED ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise?.attempt) return;
    setAnswers(exercise.attempt.answers || {});
  }, [isPreview, exercise?.attempt]);

  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise?._id, isPreview]);

  /* ================= HELPERS ================= */
  const normalize = (text = "", requireCap = false) => {
    const t = String(text || "").trim();
    return requireCap ? t : t.toLowerCase();
  };

 const getSpeakerText = (q) => {
  const combined = [q.question, q.answer].filter(Boolean).join("\n");
  return sanitizeForSpeech(combined);
};


  /* ================= EDIT HELPERS ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("conversationQuestions", next);
  };

  const updateField = (idx, field, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[idx] = {
      question: "",
      answer: "",
      requireCapitalization: false,
      ...next[idx],
      [field]: value,
    };
    updateQuestions(next);
  };

  /* ================= STUDENT INPUT ================= */
  const handleInputChange = (idx, value) => {
    if (isEditMode || showResults) return;

    const updated = { ...answers, [idx]: value };
    setAnswers(updated);

    if (typeof setAttempt === "function") {
      setAttempt({ answers: updated, hasInteracted: true });
    }

    const allAnswered = questions.every(
      (_, i) => updated[i]?.trim()
    );

    if (allAnswered && !completedRef.current) {
      completedRef.current = true;
      checkAnswers(updated);
    }
  };

  const checkAnswers = (finalAnswers) => {
    const allCorrect = questions.every((q, idx) => {
      return (
        normalize(finalAnswers[idx], q.requireCapitalization) ===
        normalize(q.answer, q.requireCapitalization)
      );
    });

    onComplete(exercise._id, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6">
            <div className="space-y-5">
              <div className="flex gap-4 items-start">
                <div className="flex-1">
                  <TextareaField
                    label={`Conversation Line ${idx + 1}`}
                    value={q.question || ""}
                    onChange={(v) =>
                      updateField(idx, "question", v)
                    }
                    placeholder="A: Hello!\nB: How are ____?"
                    rows={5}
                    required
                    error={
                      errors?.[
                        `exercise-${exercise.index}-conv-question-${idx}`
                      ]
                    }
                  />
                </div>

                <SpeakerButton
                  text={getSpeakerText(q)}
                  lang="en-GB"
                  size="lg"
                  onPlay={() =>
                    speak(getSpeakerText(q), "en-GB")
                  }
                  onStop={stop}
                />
              </div>

              <StringField
                label="Correct Answer"
                value={q.answer || ""}
                onChange={(v) =>
                  updateField(idx, "answer", v)
                }
                placeholder="you"
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-conv-answer-${idx}`
                  ]
                }
              />

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  updateField(
                    idx,
                    "requireCapitalization",
                    c
                  )
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updateQuestions(
                    questions.filter((_, i) => i !== idx)
                  )
                }
                disabled={questions.length === 1}
              >
                Remove Line
              </Button>
            </div>
          </Card>
        ))}

        <Button variant="outline" onClick={() =>
          updateQuestions([
            ...questions,
            {
              question: "",
              answer: "",
              requireCapitalization: false,
            },
          ])
        }>
          Add Conversation Line
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No conversation available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Complete the Conversation
        </CardTitle>
        <CardDescription>
          Type the missing word(s) to complete each line.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, idx) => {
          const userAnswer = answers[idx] || "";
          const isCorrect =
            showResults &&
            normalize(userAnswer, q.requireCapitalization) ===
              normalize(q.answer, q.requireCapitalization);

          return (
            <div key={idx} className="space-y-6">
              {/* Conversation */}
              <div className="rounded-xl border bg-muted/40 p-6 text-base leading-relaxed">
                <pre className="whitespace-pre-wrap font-sans">
                  {q.question || "Conversation missing."}
                </pre>
              </div>

              {/* Speaker */}
              <div className="flex justify-center">
                <SpeakerButton
                  text={getSpeakerText(q)}
                  lang="en-GB"
                  size="lg"
                  onPlay={() =>
                    speak(getSpeakerText(q), "en-GB")
                  }
                  onStop={stop}
                />
              </div>

              {/* Input */}
              <div className="max-w-md mx-auto space-y-3">
                <Input
                  value={userAnswer}
                  onChange={(e) =>
                    handleInputChange(idx, e.target.value)
                  }
                  placeholder="Type your answer..."
                  disabled={showResults}
                  className={cn(
                  "text-center text-lg font-medium transition border",
                  showResults && isCorrect && "bg-green-100 border-green-300",
                  showResults && !isCorrect && "bg-red-100 border-red-300"
                )}
                />

                {showResults && (
                  <p
                    className={cn(
                      "text-center font-semibold",
                      isCorrect
                        ? "text-green-600"
                        : "text-red-600"
                    )}
                  >
                    {isCorrect
                      ? "Correct ✅"
                      : `Incorrect — Correct answer: ${q.answer}`}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {showResults && (
          <div className="text-center pt-8">
            <p className="text-xl font-bold text-blue-700">
              Exercise Complete 🎉
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
